/** /tag_category_schemas/ — reusable tag-category JSON schemas (system-wide).
 *
 * Hand-authored wrapper (console convention: feature modules never depend on the
 * generated types). Deletes 409 when a category / set item still references the
 * schema. The `schema` blob carries our custom `x-` vocabulary (see
 * `@/features/tags/schema/vocabulary`).
 */
import type { TagSchema } from '@/features/tags/schema/vocabulary';

import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { PaginatedResponse } from './users';

export interface TagSchemaListItem {
  id: number;
  name: string;
  codename: string;
  description: string;
  category_count: number;
  created_ts: string;
  updated_ts: string;
}

export interface TagSchemaUsedBy {
  categories: number;
  set_items: number;
}

export interface TagSchemaDetail extends TagSchemaListItem {
  schema: TagSchema | null;
  used_by: TagSchemaUsedBy;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
}

export interface TagSchemaWrite {
  name?: string;
  codename?: string;
  description?: string;
  schema?: TagSchema | null;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<TagSchemaListItem>> {
  return apiGet<PaginatedResponse<TagSchemaListItem>>(`/tag_category_schemas/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<TagSchemaDetail> {
  return apiGet<TagSchemaDetail>(`/tag_category_schemas/${id}/`);
}

export function create(body: TagSchemaWrite): Promise<TagSchemaDetail> {
  return apiPost<TagSchemaDetail>('/tag_category_schemas/', body);
}

export function update(id: number, body: TagSchemaWrite): Promise<TagSchemaDetail> {
  return apiPatch<TagSchemaDetail>(`/tag_category_schemas/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/tag_category_schemas/${id}/`);
}
